import {acceptHMRUpdate, defineStore} from 'pinia'
import {StaticData} from 'src/classes/StaticData.js'
import {ref} from 'vue'
import {useAuthStore} from 'src/stores/AuthStore.js'
import axios from 'axios'
import URLHelper from "src/classes/URLHelper.js";
import download from "downloadjs";
import {logger} from 'src/boot/logger';

export const useQueryStore = defineStore('query', () => {
  // state
  const selQueryRow = ref({})   // selected query row in resultset, stored to go back and reselect the row
  const selQueryRowScrollPos = ref({})   // scroll position of selected query
  const queryResultRows = ref([])
  const queryColumns = ref([])
  const actionList = ref([])
  const breadcrumbList = ref([])
  const parameterCache = ref([])
  const catNavTree = ref({})
  const viewMode = ref(StaticData.VIEW_MODES.SIMPLE_LIST)
  const viewModeQuery = ref(StaticData.VIEW_MODES_QUERY.TABLE)

  function getSelQueryRowScrollPos(queryID) {
    if (!queryID) {
      console.error('QueryStore getSelQueryRowScrollPos - queryID is null!')
    }
    return selQueryRowScrollPos[queryID] ? selQueryRowScrollPos[queryID] : -1
  }

  function getParameterFromCache(url, queryID) {
    let result = null
    parameterCache.value.forEach((param) => {
      if (param.url == url && param.queryID == queryID) {
        result = param.paramFields
      }
    })
    return result
  }

  function getSelQueryRowAll() {
    return selQueryRow
  }

  function getSelQueryRow(queryID) {
    if (!queryID) {
      console.error('QueryStore getSelQueryRow - queryID is null!')
    }
    return selQueryRow[queryID] ? selQueryRow[queryID] : []
  }

  function setSelQueryRow({queryID, selQueryRowVal}) {
    if (!queryID) {
      console.error('QueryStore setSelQueryRow - queryID is null!')
    }
    selQueryRow[queryID] = selQueryRowVal
  }

  function remSelQueryRow({queryID, selQueryRow}) {
    if (!queryID) {
      console.error('QueryStore remSelQueryRow - queryID is null!')
    }
    if (selQueryRow[queryID]) {
      selQueryRow[queryID] = selQueryRow[queryID].filter((x) => x.__key__ != selQueryRow.__key__)
    }
  }

  function clearSelQueryRow({queryID}) {
    if (!queryID) {
      console.error('QueryStore clearSelQueryRow - queryID is null!')
    }
    if (selQueryRow[queryID]) {
      selQueryRow[queryID] = []
    }
  }

  function setSelQueryRowScrollPos({queryID, scrollPos}) {
    if (!queryID) {
      console.error('QueryStore setSelQueryRowScrollPos - queryID is null!')
    }
    selQueryRowScrollPos[queryID] = scrollPos
  }

  function removeBreadcrumb(index) {
    let spliceStart = breadcrumbList.value.length - index
    breadcrumbList.value.splice(-spliceStart, spliceStart)
  }

  function addParameterToCache(paramObj) { // { url, paramFields}
    parameterCache.value.push(paramObj)
  }

  function clearParameterCache() {
    parameterCache.value = []
  }

  async function getQueryMeta(queryID) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    let tokendata = JSON.parse(atob(authStore.sessionToken.split('.')[1]))
    let params = '?sessionID=' + encodeGUID(tokendata.sessionID) +
      '&siteID=' + encodeGUID(authStore.siteID) +
      '&queryID=' + encodeGUID(queryID)
    try {
      // TODO refactor
      // wegen session? Optimierungsmöglichkeit: session-token mitschicken
      // so kann session und site im backend ausgelesen werden und frontend schickt nur ID mit
      // keine sessionID im "klartext"
      const response = await axios.post('/queryGet' + params)
      return response.data
    } catch (e) {
      console.error('getQueryMeta Error: ', e)
      throw e
    }
  }

  async function loadQryParameter(queryID) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/queryParamListGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        queryID: queryID,
        loginname: authStore.loginname
      })
      return response.data.sqlParam
    } catch (e) {
      console.warn(
        'queryParamListGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function valueListParameterSQLChange({fieldKeyValueList, paramID, queryID}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/valueListParameterSQLChange', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        loginname: authStore.loginname,
        fieldKeyValueList: fieldKeyValueList,
        paramID: paramID,
        queryID: queryID
      })
      return response.data
    } catch (e) {
      console.warn(
        'valueListParameterSQLChange Error: ' + e.response.data.sErrorMsg + ', iResult: ' +
        e.response.data.iResult)
      throw e
    }
  }

  async function loadQryActionList(queryID) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/queryActionListGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        queryID: queryID,
        loginname: authStore.loginname,
        personID: authStore.personID
      })
      return response.data.resultSetAction
    } catch (e) {
      console.warn('queryActionListGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function execQuery({queryID, queryParameter, retrieveOnlyKeyRow, keyRowIdx, sideQuery = false}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/queryExecuteComplete', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        queryID: queryID,
        queryParameter: queryParameter,
        loginname: authStore.loginname,
        sideQuery: sideQuery,
        key: retrieveOnlyKeyRow
      })
      if (response.data.queryResultCached) {
        // let cachedResponse = JSON.parse(resp.data.queryResultCached)

        let cols = JSON.parse(response.data.queryResultCached.substr(0, response.data.queryResultCached.indexOf('}') + 1))
        let rows = JSON.parse(response.data.queryResultCached.substr(response.data.queryResultCached.indexOf('}') + 1))

        // this.$log.info("-------------- cachedResponse: cols", cols)
        // this.$log.info("-------------- cachedResponse: rows", rows)

        let parsedCols = []
        cols.colnames.map(value => parsedCols.push({
            title: value.substr(0, value.indexOf('|')),
            dataTypeID: 11,
            detailviewHidden: false,
            label: false,
            lengthMax: 0,
            lengthMin: 0,
            name: value.substr(0, value.indexOf('|')),
            projectionExport: false,
            requiredMode: false,
            valuelistType: 0,
            allowColumnDrop: true,
            allowColumnReorder: true,
            chartColor: 0,
            chartIsLegend: false,
            colorSelected: ['0'],
            columnWidth: 100,
            primaryKey: true,
            primaryKeyField: '<KEY>',
            projectionChipType: 0,
            projectionCommaPost: 4,
            projectionCommaPre: 6,
            projectionEappLayoutpos: 0,
            projectionFormat: 0,
            projectionHalign: -1,
            projectionIsGroupCol: false,
            projectionMicrochartType: 0,
            projectionMobileview: 1,
            projectionPostUpdateBehaviour: 3,
            projectionReadermode: false,
            projectionRequestParam: false,
            projectionRequired: false,
            projectionSecondline: false,
            projectionUpdateable: false,
            projectionUseAsFilename: false,
            projectionValuelistDbconIdSelected: [null],
            projectionValuelistStrict: false,
            punctuation: false,
            type: 2
          })
        )
        queryColumns.value = parsedCols

        let parsedRows = []
        rows.rows.map(value => parsedRows.push(
          {cell: value.cell})
        )
        queryResultRows.value = parsedRows

      } else {
        queryColumns.value = response.data.sqlColumn

        if(retrieveOnlyKeyRow == null || response?.data?.rows?.length > 1){
          if(retrieveOnlyKeyRow != null && response?.data?.rows?.length > 1) logger.info("!!!!!!!!!! queryExecuteComplete: retrieveOnlyKeyRow ist set, but result rows > 1:", response.data.rows)
          queryResultRows.value = response.data.rows

        } else{
          logger.info("queryExecuteComplete: optimized retrieveOnlyKeyRow executed")
          let oldKeyRowIdx = keyRowIdx.replace("i","")
          queryResultRows.value.splice(oldKeyRowIdx, 1)

          // response.data.resultKeyRowPos is position of key row in resultset
          queryResultRows.value.splice(response.data.resultKeyRowPos, 0, response.data.rows[0])
        }

        // this.$log.info("-------------- setQueryColumns: cols", resp.data.sqlColumn)
        // this.$log.info("-------------- setQueryResultRows: rows", resp.data.rows)
      }

      return response.data.outputMsg
    } catch (e) {
      console.warn('queryExecute Error: ', e)
      throw e
    }
  }

  async function queryExecuteAndExport({queryID, queryParameter, extension, preview = false}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/queryExecuteAndExport',
        method: 'POST',
        responseType: 'blob',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          queryID: queryID,
          queryParameter: queryParameter,
          loginname: authStore.loginname,
          extension: extension
        }
      })
      let filename = URLHelper.getFilenameFromHeader(response.headers['content-disposition'])
      if (preview) return {filename, data: response.data}
      download(response.data, filename)
      return filename
    } catch (error) {
      // console.error('queryExecuteAndExport Error: ' + e.response.data.sErrorMsg + ', iResult: ' + error.response.data.iResult)
      error.response.data = await URLHelper.readBlobAsJson(error.response.data);
      throw error;
    }
  }

  async function queryRecordReorderUpDown({queryID, queryParameter, selectedRow, key, nextRow, prevRow, updown}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/queryRecordReorderUpDown',
        method: 'POST',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          queryID: queryID,
          queryParameter: queryParameter,
          loginname: authStore.loginname,
          updown: updown,
          selectedRow: selectedRow,
          key: key,
          nextRow: nextRow,
          prevRow: prevRow
        }
      })
    } catch (error) {
      // console.error('queryRecordReorderUpDown Error: ' + e.response.data.sErrorMsg + ', iResult: ' + error.response.data.iResult)
      console.error('queryRecordReorderUpDown Error: ' + error)
      throw error;
    }
  }

  async function execAndDownload({queryID, queryParameter}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/queryExecuteAndDownload',
        method: 'POST',
        responseType: 'blob',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          queryID: queryID,
          queryParameter: queryParameter,
          loginname: authStore.loginname
        }
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      let filename = response.headers['content-disposition'].substr(20)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      return filename
    } catch (e) {
      console.warn(
        'queryExecuteAndDownload Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function execAndDownloadOds({queryID, queryParameter}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/queryExecuteAndDownloadOds',
        method: 'POST',
        responseType: 'blob',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          queryID: queryID,
          queryParameter: queryParameter,
          loginname: authStore.loginname
        }
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      let filename = response.headers['content-disposition'].substr(20)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      return filename
    } catch (e) {
      console.warn(
        'queryExecuteAndDownload Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function execAndDownloadCsv({queryID, queryParameter, separator, stringEnclosure}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/queryExecuteAndDownloadCsv',
        method: 'POST',
        responseType: 'blob',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          queryID: queryID,
          queryParameter: queryParameter,
          loginname: authStore.loginname,
          separator: separator,
          stringEnclosure: stringEnclosure
        }
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      let filename = response.headers['content-disposition'].substr(20)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      return filename
    } catch (e) {
      console.warn(
        'queryExecuteAndDownload Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  /**
   * Creates a result object with the specified parameters.
   *
   * @param {string|null} sOutput - The output data as a string. Defaults to null.
   * @param {string|null} filename - The name of the file, if applicable. Defaults to null.
   * @param {any|null} data - Additional data associated with the result. Defaults to null.
   * @param {number|null} iResult - The numerical result code. Defaults to null.
   * @param {string|null} sOutputMsg - The output message describing the result. Defaults to null.
   * @param {string|null} sErrorMsg - The error message, if any. Defaults to null.
   * @param {string|null} sStacktrace - The error stacktrace, if any. Defaults to null.
   * @return {Object} An object containing the provided parameters as properties.
   */
  function createResult(sOutput = null, filename = null, data = null, iResult = null, sOutputMsg = null, sErrorMsg = null, sStacktrace = null) {
    return {
      sOutput,
      filename,
      data,
      iResult,
      sOutputMsg,
      sErrorMsg,
      sStacktrace
    };
  }

  async function execQueryAction({queryID, key, racID, selectedRow, queryParameter}) {
    let result = null
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/executeQueryAction',
        method: 'POST',
        responseType: 'blob',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          queryID: queryID,
          loginname: authStore.loginname,
          key: key,
          racID: racID,
          personID: authStore.personID,
          selectedRow: selectedRow,
          queryParameter: queryParameter
        }
      })
      if (response && response.data) {
        const filename = parseFilename(response.headers["content-disposition"])

        if (response.data.type === 'application/json') {
          const text = await response.data.text();
          const jsonData = JSON.parse(text);
          result = createResult(jsonData.sOutput, filename, response.data, jsonData.iResult, jsonData.sOutputMsg, jsonData.sErrorMsg)

        } else {
          result = createResult(response.data.sOutput, filename, response.data, response.data.iResult, response.data.sOutputMsg, response.data.sErrorMsg)
        }
      }

    } catch (error) {
      error.response.data = await URLHelper.readBlobAsJson(error.response.data);
      throw error;
    }

    return result
  }

  /**
   * Executes a plugin call
   *
   * @param {Object} params - The parameters for executing the plugin call.
   * @param {string} params.objectInstanceID - The ID of the object instance for the plugin operation.
   * @param {string} params.objClassID - The ID of the object class associated with the plugin operation.
   * @param {string} params.pluginID - The unique identifier of the plugin to call.
   * @param {Object} params.propValues - The property values to be passed as input to the plugin.
   * @param {Object} params.pluginContext - The context information for the plugin execution.
   * @param {Object} params.pluginParams - Additional parameters required by the plugin.
   * @param {Array} params.multiSelectKeys - A list of keys selected for multiple operations, if applicable.
   * @param {Array} params.visibleColumns - A list of visible columns in the data context for the plugin.
   * @return {Promise<Blob>} Returns a promise resolving to the response Blob representing the result of the operation.
   */
  async function execPluginCall({
                                  objectInstanceID,
                                  objClassID,
                                  pluginID,
                                  propValues,
                                  pluginContext,
                                  pluginParams,
                                  multiSelectKeys,
                                  visibleColumns
                                }) {
    let result = null
    try {
      const authStore = useAuthStore()
      await authStore.sessionTouch();

      const response = await axios({
        url: "/callPlugin",
        method: 'POST',
        responseType: 'blob',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          loginname: authStore.loginname,
          objectInstanceID,
          objClassID,
          pluginID,
          propValues,
          pluginContext,
          pluginParams,
          multiSelectKeys,
          visibleColumns
        }
      })
      if (response && response.data) {
        const filename = parseFilename(response.headers["content-disposition"])
        result = createResult(response.data.sOutput, filename, response.data, response.data.iResult, response.data.sOutputMsg, response.data.sErrorMsg)
      }

    } catch (error) {
      if (error && error.response && error.response.data) {
        error.response.data = await URLHelper.readBlobAsJson(error.response.data);
      }
      throw error;
    }
    return result
  }

  async function downloadFile(fileConnectionString) {
    let result = null
    try {
      // this.$log.info("queryStore downloadFile - fileConnectionString: " + fileConnectionString)
      const authStore = useAuthStore()
      await authStore.sessionTouch()

      const response = await axios({
        url: '/downloadFile',
        method: 'POST',
        responseType: 'blob',
        data: {
          "sessionToken": authStore.sessionToken,
          "siteID": authStore.siteID,
          "fileConnectionString": fileConnectionString
        }
      })
      if (response && response.headers && response.headers['content-type'] && response.headers['content-type'] === "application/octet-stream") {
        const filename = parseFilename(response.headers["content-disposition"])
        result = createResult("", filename, response.data, 0)
      }
      return result

    } catch (error) {
      error.response.data = await URLHelper.readBlobAsJson(error.response.data);
      throw error
    }
  }

  function parseFilename(contentDisposition) {
    // logger.info("parseFilename - contentDisposition: " + contentDisposition)
    if (!contentDisposition) return null;

    // Suche nach `filename=` innerhalb des Headers
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    if (filenameMatch && filenameMatch[1]) {
      // Gibt den Dateinamen zurück, falls er gefunden wird
      return filenameMatch[1];
    }
    return null;
  }

  async function queryCellUpdate({key, cellname, cellvalue, queryID, row, queryParameter}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/queryCellUpdate', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        loginname: authStore.loginname,
        queryID: queryID,
        key: key,
        cellname: cellname,
        cellvalue: cellvalue,
        row: row,
        queryParameter: queryParameter
      })
      return response.data
    } catch (e) {
      console.warn('queryCellUpdate Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  // {queryID, cellname, row, queryParameter}
  async function queryCellUpdateValueListDomainGet({cellname, queryID, row, queryParameter}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/queryCellUpdateValueListDomainGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        loginname: authStore.loginname,
        queryID: queryID,
        cellname: cellname,
        row: row,
        queryParameter: queryParameter
      })
      return response.data
    } catch (e) {
      console.warn('queryCellUpdateValueListDomainGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function categoryNavTreeGet() {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/categoryNavTreeGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID
      })
      return response.data
    } catch (e) {
      console.warn('categoryNavTreeGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function categoryNavNodeGet(categoryID) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/categoryNavNodeGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        categoryID: categoryID
      })

      return response.data
    } catch (e) {
      console.warn('categoryNavNodeGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function categoryPreExpandGet(categoryID) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/categoryPreExpandGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        categoryID: categoryID
      })
      return response.data.category
    } catch (e) {
      console.warn('categoryPreExpandGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function webEntityGet({webEntityID, webEntityName}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios.post('/webEntityGet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        webEntityName: webEntityName,
        webEntityID: webEntityID
      })
      return response.data
    } catch (e) {
      console.warn('webEntityGet Error: ' + e.response.data.sErrorMsg + ', iResult: ' + e.response.data.iResult)
      throw e
    }
  }

  async function queryFavSet({queryID, value, title}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      await axios.post('/queryFavSet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        value: value,
        queryID: queryID,
        title: title
      })
    } catch (e) {
      console.error('queryFavSet Error: ' + e)
      throw e
    }
  }

  async function categoryFavSet({categoryID, value}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      await axios.post('/categoryFavSet', {
        sessionToken: authStore.sessionToken,
        siteID: authStore.siteID,
        value: value,
        queryID: categoryID
      })
    } catch (e) {
      console.error('categoryFavSet Error: ' + e)
      throw e
    }
  }

  async function documentGetAsBase64({docID, propInstanceID}) {
    const authStore = useAuthStore()
    await authStore.sessionTouch()
    try {
      const response = await axios({
        url: '/documentGetAsBase64',
        method: 'POST',
        data: {
          sessionToken: authStore.sessionToken,
          siteID: authStore.siteID,
          docID: docID,
          propInstanceID: propInstanceID
        }
      })
      return response
    } catch (e) {
      console.error('documentGetAsBase64 Error: ' + e)
      throw e
    }
  }

  return {
    selQueryRow,
    selQueryRowScrollPos,
    queryResultRows,
    queryColumns,
    actionList,
    breadcrumbList,
    parameterCache,
    catNavTree,

    viewMode,
    viewModeQuery,

    getSelQueryRow,
    getSelQueryRowAll,
    getSelQueryRowScrollPos,
    getParameterFromCache,
    setSelQueryRow,
    remSelQueryRow,
    clearSelQueryRow,
    setSelQueryRowScrollPos,
    removeBreadcrumb,
    addParameterToCache,
    clearParameterCache,
    getQueryMeta,
    loadQryParameter,
    valueListParameterSQLChange,
    queryCellUpdateValueListDomainGet,
    loadQryActionList,
    execQuery,
    queryExecuteAndExport,
    execAndDownload,
    execAndDownloadOds,
    execAndDownloadCsv,
    queryRecordReorderUpDown,
    execQueryAction,
    execPluginCall,
    queryCellUpdate,
    categoryNavTreeGet,
    categoryNavNodeGet,
    categoryPreExpandGet,
    webEntityGet,
    queryFavSet,
    categoryFavSet,
    documentGetAsBase64,
    downloadFile
  }
})

// Enable Hot Module Replacement for stores. make sure to pass the right store definition, `useFormStore` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useQueryStore, import.meta.hot))
}
